import React, { memo, useEffect, useRef, useState } from "react";
import { Input } from "../../ui/input";
import { TableColumns } from "./Table";

type TableCellType<T> = {
  col: TableColumns<T>;
  row: T;
  disabled: boolean;
  newRowFlag?: keyof T;
  isEditMode?: boolean;
  isFirstCell?: boolean;
  isLastCell?: boolean;
  getAlignmentClass?: (align?: "Left" | "Center" | "Right") => string;
};

const TableCell = <T,>({
  col,
  row,
  disabled,
  newRowFlag,
  isEditMode = false,
  isFirstCell = false,
  isLastCell = false,
  getAlignmentClass
}: TableCellType<T>) => {
  const newRow = newRowFlag && row[newRowFlag];
  const tdRef = useRef<HTMLTableCellElement>(null);
  const dataName = col.dataName;
  const dataNameJSX = col.dataNameJSX;

  const cellValue = dataName ? String(row[dataName]) : "";
  const [text, setText] = useState<string>(cellValue);
  const prevCellValueRef = useRef<string>(cellValue);

  useEffect(() => {
    const newCellValue = dataName ? String(row[dataName]) : "";
    if (prevCellValueRef.current !== newCellValue) {
      setText(newCellValue);
      prevCellValueRef.current = newCellValue;
    }
  }, [dataName, row]);

  useEffect(() => {
    if (text === cellValue || !dataName) {
      return;
    }

    const value = text as unknown as T[keyof T];
    row[dataName] = value;
  }, [text]);

  const getCellClasses = () => {
    const classes = [
      `Align${col.align ?? "Left"}`,
      "text-collins-md py-panel-padding pl-panel-padding border-b border-button-default break-words",
      getAlignmentClass?.(col.align) ?? "text-left",
      col.className ?? ""
    ];

    if (isEditMode) {
      classes.push("table-edit-row-cell");
      if (isFirstCell) classes.push("table-edit-row-cell-first");
      if (isLastCell) classes.push("table-edit-row-cell-last");
    }

    return classes.filter(Boolean).join(" ");
  };

  if (!dataName) {
    if (dataNameJSX) {
      const element = row[dataNameJSX] as unknown as JSX.Element;
      return <td className={getCellClasses()}>{element}</td>;
    }
    return <td className={getCellClasses()} />;
  }

  const width =
    !tdRef.current || newRow
      ? "100%"
      : `calc(${tdRef.current.clientWidth}px - var(--panelPadding)`;

  return (
    <td className={getCellClasses()} ref={tdRef}>
      {disabled || !col.editable ? (
        <div className="TableCell-Content table-cell-content">
          <span className={`icon w-fit ${text && disabled ? "" : "opacity-0"}`}>
            {col.leftIcon}
            {col.leftIconDataName &&
              (row[col.leftIconDataName] as React.ReactNode)}
          </span>
          <span>{text}</span>
        </div>
      ) : (
        <Input
          style={{
            width: `${width}`
          }}
          value={text}
          type={col.type}
          maxLength={256}
          onChange={(e) => {
            setText(e.target.value);
          }}
          className="table-input h-9 px-3 py-2 text-collins-md"
        />
      )}
    </td>
  );
};

export default memo(TableCell) as typeof TableCell;
